import type { PaymentPeriod } from '@kimuno/api-sdk/lib/types/common';
import type { DiscountInformation } from '@kimuno/api-sdk/lib/types/responses/Billing';
import CancelIcon from '@mui/icons-material/Cancel';
import { Box, Chip, TableCell, Typography } from '@mui/material';
import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { palette, spaces } from '../../../assets/styles/themes/tokens';
import { currencyFormat } from '../../../utils/format';
import PromoCodeInput from './PromoCodeInput';

enum FormLayout {
	initial = 0,
	active = 1,
	confirmed = 2,
}

interface OwnProps {
	licenceId: number;
	paymentRhythm: PaymentPeriod;
	onPromoCodeValidation: (discountInformation: DiscountInformation, promoCode: string) => void;
	onActiveLayoutInit: () => void;
	initPromoCode?: string;
}

const PromoCode: React.FC<OwnProps> = (props) => {
	const { t } = useTranslation();
	const [formLayout, setFormLayout] = useState<FormLayout>(FormLayout.initial);
	const [discountInformation, setDiscountInformation] = useState<DiscountInformation>();
	const [submittedPromoCode, setSubmittedPromoCode] = useState<string>();
	const [initPromoCode, setInitPromoCode] = useState<string>();

	// We need to make sure that the promo code supports the payment rythm selected by the user.
	const [isPaymentRythmValid, setIsPaymentRythmValid] = useState(true);

	useEffect(() => {
		if (discountInformation?.billingPeriods) {
			setIsPaymentRythmValid(discountInformation.billingPeriods.includes(props.paymentRhythm));
		}
	}, [props.paymentRhythm, discountInformation]);

	// biome-ignore lint/correctness/useExhaustiveDependencies: pre-Biome hook deps kept as-is; changing the deps list alters effect timing
	useEffect(() => {
		if (props.initPromoCode) {
			setInitPromoCode(props.initPromoCode);
			setFormLayout(FormLayout.active);
		}
	}, []);

	const InitialLayout = () => {
		const onClick = () => setFormLayout(FormLayout.active);

		return (
			<TableCell sx={{ paddingX: 0 }} colSpan={2}>
				<Box onClick={onClick} sx={{ cursor: 'pointer' }} data-testid="promo-code-form-initial-layout">
					<Typography variant="body2">{t('Add discount code')}</Typography>
				</Box>
			</TableCell>
		);
	};

	const ConfirmedLayout = () => {
		if (!isPaymentRythmValid) {
			setFormLayout(FormLayout.active);
		}

		const onDeleteClick = () => {
			setInitPromoCode(undefined);
			setFormLayout(FormLayout.active);
		};

		return (
			<TableCell sx={{ paddingX: 0 }} colSpan={2}>
				<Box
					sx={{
						display: 'flex',
						justifyContent: 'space-between',
						alignItems: 'center',
					}}
				>
					<Box display="flex" alignItems="center" gap={spaces.sm}>
						<Typography variant="body2" color="textSecondary">
							{`${t('Discount')}`}
						</Typography>
						<Chip
							data-testid="promo-code-chip"
							color="default"
							size="medium"
							variant="filled"
							sx={{
								'& .MuiChip-label': {
									color: palette.text.primary,
									fontWeight: 500,
								},
							}}
							label={submittedPromoCode}
							onDelete={onDeleteClick}
							deleteIcon={<CancelIcon fontSize="small" data-testid="remove-promo-code-button" />}
						/>
					</Box>
					{discountInformation?.priceReduction && (
						<Typography variant="body2" data-testid="promo-code-form__price-reduction">
							-{currencyFormat(discountInformation?.priceReduction)}
						</Typography>
					)}
				</Box>
			</TableCell>
		);
	};

	return (
		<>
			{formLayout === FormLayout.initial && <InitialLayout />}
			{formLayout === FormLayout.active && (
				<TableCell sx={{ paddingX: 0 }} colSpan={2} align="left">
					<PromoCodeInput
						isPaymentRythmValid={isPaymentRythmValid}
						submittedPromoCode={submittedPromoCode}
						setSubmittedPromoCode={setSubmittedPromoCode}
						setDiscountInformation={setDiscountInformation}
						setFormLayout={setFormLayout}
						licenceId={props.licenceId}
						paymentRhythm={props.paymentRhythm}
						onPromoCodeValidation={props.onPromoCodeValidation}
						onActiveLayoutInit={props.onActiveLayoutInit}
						initPromoCode={initPromoCode}
					/>
				</TableCell>
			)}
			{formLayout === FormLayout.confirmed && <ConfirmedLayout />}
		</>
	);
};

export default PromoCode;
